import { ReservationDocument, ReservationQuery, ReservationQueryVariables } from "@graphql/api";
import Booking from "@lib/classes/Booking";
import { getContent } from "@lib/use_content";
import Cookies from "js-cookie";

function dropTypename(object) {
  const newObj = { ...object };
  if (newObj.__typename) {
    delete newObj.__typename;
  }
  return newObj;
}

export default async function readRes({
  resId,
  pid,
}: {
  resId?: number;
  pid?: string;
}): Promise<be.Booking | null> {
  const client = global.window.apolloClient;
  const queryVars: ReservationQueryVariables = { pid, resId };
  const { data }: { data: ReservationQuery } = await client.query({
    fetchPolicy: "network-only",
    query: ReservationDocument,
    variables: queryVars,
  });

  if (!data.reservation) return null;
  const res = data.reservation;

  // Get service area name (for analytics)
  let serviceAreaName;
  if (res.serviceArea) {
    let { data: content, error: cError } = await getContent(["serviceAreaName"], res.serviceArea);
    serviceAreaName = content?.content?.serviceAreaName;
  }

  const creditCards: be.TokenizedCreditCard[] = [];
  if (res.payment?.creditCard?.token?.length) {
    creditCards.push(res.payment.creditCard);
  }

  if (res.profiles?.group) {
    // Keep the group in a cookie so that SSR can use it. Back-end should always validate it against
    // the JWT before returning any real data since a bad user could mess with it.
    Cookies.set(`b_${window.siteId}_gi`, res.profiles.group);
  }

  const booking = new Booking({
    contactInfo: res.contactInfo,
    creditCards,
    edit: true,
    itineraryId: res.itineraryId,
    legs: [
      {
        bagCount: res.bagCount,
        cancellable: res.cancellable,
        cancelled: res.cancelled,
        customerRequest: res.flight?.flightType == "R",
        dropoff: { ...dropTypename(res.dropoff), serviceArea: res.serviceArea },
        editable: res.editable,
        epicPassValidated: res.epicPassValidated,
        flags: res.flags,
        flight: {
          ...dropTypename(res.flight),
          flightType: res.flight?.flightType == "R" ? undefined : res.flight?.flightType, // We use leg.customerRequest flag to indicate cust request.
        },
        jobType: res.jobType,
        open: !!res.open,
        options: { ...dropTypename(res.options || {}) },
        passengers: res.passengers.map((p: be.FieldValue) => dropTypename(p)),
        pickup: { ...dropTypename(res.pickup), serviceArea: res.serviceArea },
        requestedPickupTime: res.service?.selectedTime?.pickupTime,
        resId: res.resId,
        resIdInternal: res.resIdInternal,
        returnTripStatus: res.returnTripStatus,
        service: dropTypename(res.service),
        serviceExtras: res.serviceExtras.map((se: be.FieldValue) => dropTypename(se)),
        sourceType: Number.isInteger(res.sourceType) ? res.sourceType : 60,
        stops: res.stops,
        userGratuityOverride: !!res.userGratuityOverride,
        userGratuityPercentage: res.userGratuityPercentage,
      },
    ],
    originalPayment: dropTypename(res.payment),
    otherInfo: { ...dropTypename(res.other), epicPassId: res.epicPassId },
    payment: dropTypename(res.payment),
    profiles: dropTypename(res.profiles),
    roundtrip: false,
    serviceArea: res.serviceArea,
    serviceAreaName: serviceAreaName,
    totalOriginal: res.totalOriginal,
  });

  if (booking.legs[0].service?.selectedTime)
    booking.legs[0].service.selectedTime = {
      ...booking.legs[0].service.selectedTime,
      customerSelected: true,
    };

  return booking;
}
